import { useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  FileText, Download, Loader2, ChevronDown, ChevronRight,
  Building2, Briefcase, Calculator, Edit3, MessageSquare,
  Send, Sparkles, CheckCircle2, Info
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const num = Number(v);
  if (isNaN(num)) return String(v);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(num);
};

const FORM_TYPES = [
  {
    value: "1040", label: "Form 1040", icon: FileText,
    subtitle: "U.S. Individual Income Tax Return",
    description: "Main return — pulls W-2 wages, 1099 income, deductions, credits, and tax liability",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20"
  },
  {
    value: "schedule_c", label: "Schedule C", icon: Briefcase,
    subtitle: "Profit or Loss From Business",
    description: "Self-employment — pulls 1099-NEC income and categorized business expenses",
    color: "text-purple-400 bg-purple-500/10 border-purple-500/20"
  },
  {
    value: "schedule_e", label: "Schedule E", icon: Building2,
    subtitle: "Supplemental Income and Loss",
    description: "Rental real estate — pulls property data, rental income, mortgage interest, depreciation",
    color: "text-orange-400 bg-orange-500/10 border-orange-500/20"
  },
];

function FormSection({ section, edits, onEdit, onAskAI }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border border-white/5 rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white/[0.02] hover:bg-white/[0.03] transition-colors"
      >
        <span className="text-sm font-semibold text-white">{section.title}</span>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="divide-y divide-white/5">
          {section.fields.map((field) => {
            const editedValue = edits[field.key];
            const displayValue = editedValue !== undefined ? editedValue : field.value;
            const isCalculated = !field.editable;
            const isNum = typeof field.value === "number";

            return (
              <div key={field.key} className={`flex items-center gap-3 px-4 py-2.5 ${isCalculated ? "bg-white/[0.02]" : ""}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${isCalculated ? "font-semibold text-white" : "text-zinc-400"}`}>
                    {field.label}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {field.editable ? (
                    <Input
                      data-testid={`field-${field.key}`}
                      type={isNum ? "number" : "text"}
                      step={isNum ? "0.01" : undefined}
                      value={editedValue !== undefined ? editedValue : field.value}
                      onChange={(e) => onEdit(field.key, isNum ? parseFloat(e.target.value) || 0 : e.target.value)}
                      className="w-36 text-right bg-black/30 border-white/10 rounded-sm text-sm font-mono"
                    />
                  ) : (
                    <span className={`text-sm font-mono font-bold w-36 text-right ${
                      isNum && displayValue < 0 ? "text-red-400" : "text-white"
                    }`}>
                      {isNum ? formatCurrency(displayValue) : displayValue}
                    </span>
                  )}
                  {field.editable && (
                    <button
                      onClick={() => onAskAI(field.label)}
                      className="text-zinc-600 hover:text-primary transition-colors"
                      title="Ask AI about this field"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AIFormFiller() {
  const { api } = useAuth();
  const [selectedForm, setSelectedForm] = useState(null);
  const [formData, setFormData] = useState(null);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [filingStatus, setFilingStatus] = useState("single");
  const [taxYear, setTaxYear] = useState("2026");

  // AI Chat
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiContext, setAiContext] = useState("");

  const generateForm = async (formType) => {
    setLoading(true);
    setFormData(null);
    setEdits({});
    setSelectedForm(formType);
    setAiAnswer("");
    try {
      const res = await api.post("/ai/form-filler/generate", {
        form_type: formType,
        tax_year: parseInt(taxYear),
        filing_status: filingStatus,
      });
      setFormData(res.data);
      toast.success(`${formType.toUpperCase()} pre-filled from your data`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate form");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!formData) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/ai/form-filler/download-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          form_type: formData.form_type,
          tax_year: parseInt(taxYear),
          filing_status: filingStatus,
          edits: edits,
        }),
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WealthHub_${formData.form_type}_${taxYear}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleEdit = (key, value) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
  };

  const askAI = async (fieldLabel) => {
    if (!aiQuestion.trim() && !fieldLabel) return;
    setAiLoading(true);
    setAiContext(fieldLabel || aiContext);
    try {
      const res = await api.post("/ai/form-filler/ai-assist", {
        form_type: formData?.form_type || "1040",
        question: aiQuestion || `What should I enter for "${fieldLabel}"?`,
        field_context: fieldLabel || aiContext,
      });
      setAiAnswer(res.data.answer);
      setAiQuestion("");
    } catch {
      toast.error("AI assist failed");
    } finally {
      setAiLoading(false);
    }
  };

  const openAIForField = (fieldLabel) => {
    setAiContext(fieldLabel);
    setAiQuestion(`What should I enter for "${fieldLabel}"?`);
  };

  const editCount = Object.keys(edits).length;

  return (
    <Layout title="AI Form Filler">
      <div data-testid="ai-form-filler-page" className="space-y-6">
        {/* Config Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Select value={taxYear} onValueChange={setTaxYear}>
            <SelectTrigger data-testid="form-filler-year" className="w-28 bg-black/50 border-white/10 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filingStatus} onValueChange={setFilingStatus}>
            <SelectTrigger data-testid="form-filler-filing" className="w-44 bg-black/50 border-white/10 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="single">Single</SelectItem>
              <SelectItem value="married_jointly">Married (Joint)</SelectItem>
              <SelectItem value="married_separately">Married (Sep)</SelectItem>
              <SelectItem value="head_of_household">Head of Household</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Form Selector Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FORM_TYPES.map((ft) => {
            const Icon = ft.icon;
            const isSelected = selectedForm === ft.value;
            const [textColor, bgColor, borderColor] = ft.color.split(" ");
            return (
              <button
                key={ft.value}
                data-testid={`form-select-${ft.value}`}
                onClick={() => generateForm(ft.value)}
                disabled={loading}
                className={`text-left p-5 rounded-sm border transition-all ${
                  isSelected ? `${borderColor} bg-white/[0.03]` : "border-white/10 bg-[#0A0A0A] hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${textColor}`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{ft.label}</p>
                    <p className="text-[10px] text-zinc-500">{ft.subtitle}</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-1">{ft.description}</p>
                {isSelected && formData && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-emerald-400">
                    <CheckCircle2 className="w-3 h-3" /> Pre-filled
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary animate-spin mr-3" />
            <span className="text-zinc-400">Pre-filling form from your data...</span>
          </div>
        )}

        {/* Form Fields */}
        {formData && !loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form sections */}
            <div className="lg:col-span-2 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-lg font-bold text-white">{formData.form_title}</h2>
                  <p className="text-xs text-zinc-500">
                    Tax Year {formData.tax_year} | {(formData.filing_status || filingStatus).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                    {editCount > 0 && <span className="text-primary ml-2">({editCount} edits)</span>}
                  </p>
                </div>
                <Button
                  data-testid="download-pdf-btn"
                  onClick={downloadPDF}
                  disabled={downloading}
                  className="bg-primary text-black font-bold rounded-sm hover:bg-primary/90"
                >
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download PDF
                </Button>
              </div>

              {formData.sections.map((section, i) => (
                <FormSection
                  key={i}
                  section={section}
                  edits={edits}
                  onEdit={handleEdit}
                  onAskAI={openAIForField}
                />
              ))}

              {/* Disclaimer */}
              <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-sm">
                <p className="text-[10px] text-amber-400 flex items-start gap-2">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  This is a reference document pre-filled from your WealthHub data. It is NOT an official IRS filing. Review all fields carefully and consult a tax professional before filing.
                </p>
              </div>
            </div>

            {/* AI Assistant Panel */}
            <div className="space-y-3">
              <Card className="bg-[#0A0A0A] border-white/10 rounded-none sticky top-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    AI Form Assistant
                  </CardTitle>
                  <p className="text-[10px] text-zinc-500">Ask about any field or tax rule</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {aiContext && (
                    <div className="text-[10px] text-zinc-500 bg-white/[0.02] p-2 rounded">
                      Context: <span className="text-zinc-300">{aiContext}</span>
                    </div>
                  )}

                  {aiAnswer && (
                    <div data-testid="ai-answer" className="p-3 bg-primary/5 border border-primary/10 rounded text-xs text-zinc-300 leading-relaxed">
                      {aiAnswer}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Input
                      data-testid="ai-question-input"
                      placeholder="Ask about a field..."
                      value={aiQuestion}
                      onChange={(e) => setAiQuestion(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && askAI()}
                      className="bg-black/30 border-white/10 rounded-sm text-sm"
                    />
                    <Button
                      data-testid="ai-ask-btn"
                      onClick={() => askAI()}
                      disabled={aiLoading || !aiQuestion.trim()}
                      size="sm"
                      className="bg-primary text-black rounded-sm px-3"
                    >
                      {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    </Button>
                  </div>

                  <div className="space-y-1">
                    <p className="text-[10px] text-zinc-600">Quick questions:</p>
                    {[
                      "What deductions can I claim?",
                      "Should I itemize or take standard?",
                      "How is self-employment tax calculated?",
                    ].map((q) => (
                      <button
                        key={q}
                        onClick={() => {
                          setAiQuestion(q);
                          askAI();
                        }}
                        className="block w-full text-left text-[10px] text-zinc-500 hover:text-primary transition-colors py-0.5"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!formData && !loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-20 h-20 rounded-full bg-white/[0.02] border border-white/5 flex items-center justify-center mb-4">
              <FileText className="w-10 h-10 text-zinc-600" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Select a Form to Pre-Fill</h3>
            <p className="text-sm text-zinc-500 text-center max-w-md">
              Choose a tax form above. WealthHub will automatically populate it with your W-2s, 1099s, property data, and expenses. Edit any field, then download as PDF.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
